import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Edit2, MoreVertical, Trash2, Eye, Users, CreditCard } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { JobEditDialog } from "./JobEditDialog";
import { PlanSelectionDialog } from "../PlanSelectionDialog";
import { Badge } from "@/components/ui/badge";
import type { Job } from "@/types/jobs.types";
import { supabase } from "@/integrations/supabase/client";

interface JobListItemProps {
  job: Job;
  onStatusChange: (jobId: string, newStatus: string) => void;
  onDelete: (jobId: string) => void;
  onEdit: (formData: any) => Promise<boolean>;
}

export function JobListItem({ job, onStatusChange, onDelete, onEdit }: JobListItemProps) {
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPlanDialogOpen, setIsPlanDialogOpen] = useState(false);
  const [availableCredits, setAvailableCredits] = useState(0);

  const openPlanDialog = async () => {
    const { data: credits } = await supabase
      .from('company_credits')
      .select('credits_remaining')
      .gt('credits_remaining', 0);
    const total = credits?.reduce((sum, c) => sum + c.credits_remaining, 0) || 0;
    setAvailableCredits(total);
    setIsPlanDialogOpen(true);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const handleEdit = async (formData: any) => {
    const success = await onEdit(formData);
    if (success) {
      setIsEditDialogOpen(false);
    }
  };

  return (
    <>
      <div className="border rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div className="space-y-2 min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold truncate">{job.title}</h3>
              <Badge variant={job.status === 'active' ? 'success' : job.status === 'pending_payment' ? 'warning' : 'secondary'}>
                {job.status === 'active' ? 'Ativa' : job.status === 'pending_payment' ? 'Aguardando Pagamento' : 'Inativa'}
              </Badge>
            </div>
            
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <div className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {job.applications_count ?? 0} candidatura{(job.applications_count ?? 0) !== 1 ? 's' : ''}
              </div>
              {job.views_count !== undefined && job.views_count !== null && (
                <div className="flex items-center gap-1">
                  <Eye className="h-4 w-4" />
                  {job.views_count} visualizaç{job.views_count !== 1 ? 'ões' : 'ão'}
                </div>
              )}
              {job.created_at && (
                <div>
                  Publicada em {formatDate(job.created_at)}
                </div>
              )}
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="shrink-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem 
                onSelect={(e) => {
                  e.preventDefault();
                  setIsEditDialogOpen(true);
                }}
              >
                <Edit2 className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              
              {job.status === 'pending_payment' ? (
                <DropdownMenuItem onSelect={() => openPlanDialog()}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Finalizar Pagamento
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem 
                  onSelect={() => onStatusChange(job.id, job.status === 'active' ? 'inactive' : 'active')}
                >
                  {job.status === 'active' ? 'Desativar' : 'Ativar'}
                </DropdownMenuItem>
              )}
              
              <DropdownMenuItem 
                className="text-red-600"
                onSelect={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Excluir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <JobEditDialog 
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        job={job}
        onSubmit={handleEdit}
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir vaga</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a vaga <strong>"{job.title}"</strong>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => onDelete(job.id)}
            >
              Sim, excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <PlanSelectionDialog
        open={isPlanDialogOpen}
        onOpenChange={setIsPlanDialogOpen}
        jobId={job.id}
        availableCredits={availableCredits}
        onUseCredit={() => {
          setIsPlanDialogOpen(false);
          // Trigger a refresh by calling status change to active
          onStatusChange(job.id, 'active');
        }}
      />
    </>
  );
}
